// CSP sem `unsafe-eval` (F35): antes de qualquer schema do zod (ver o módulo).
import "@eldrun/protocol/jitless";
import { classOf, isClassId } from "@eldrun/content";
import { GEN_VERSION, parseSeed } from "@eldrun/worldgen";
import { audio } from "../audio/engine.ts";
import { settings } from "../ui/panels/settings.ts";
import { readClientConfig } from "./config.ts";
import { createRenderer, UnsupportedError } from "../engine/renderer.ts";
import { parseToonStyle } from "../render/atmosphere.ts";
import { POST_PRESETS } from "../render/post/post-model.ts";
import { QUALITY } from "../render/quality.ts";
import { ModelLibrary, models } from "../render/model-library.ts";
import { AppFlow, type AppProbe } from "./flow.ts";
import { applyClassTheme, rememberedClass } from "../ui/theme/class-theme.ts";
import { ALL_AVATARS, avatars, type AvatarModelId } from "../render/avatar-library.ts";
import { AVATAR_CLIPS, AVATAR_MODELS } from "../assets/avatars.generated.ts";
import { parseAvatarScript } from "../scenes/avatar-script.ts";
import { startAvatarViewer } from "../scenes/avatar-viewer.ts";
import { surfaceFromParams } from "../scenes/surface-params.ts";
import { startCreatureShowcase } from "../scenes/creature-showcase.ts";
import { startCreatureViewer } from "../scenes/creature-viewer.ts";
import { startIslandViewer, type IslandReference } from "../scenes/island-viewer.ts";
import { startItemViewer } from "../scenes/item-viewer.ts";
import { startNeonShowcase } from "../scenes/neon-showcase.ts";
import { startUiShowcase } from "../scenes/ui-showcase.ts";
import { startSoundViewer } from "../scenes/sound-viewer.ts";
import { startSceneryShowcase } from "../scenes/scenery-showcase.ts";
import { startFlamesShowcase } from "../scenes/flames-showcase.ts";
import { startStructureViewer, type StructureViewerView } from "../scenes/structure-viewer.ts";
import "../ui/theme/app.css";
import "../ui/theme/styles.css";
import "../ui/theme/neon.css";
import "../ui/theme/kit.css";
import "../ui/theme/telas.css";
import "../ui/theme/paineis.css";
import "../ui/theme/hud.css";
import { applyPaletteTokens } from "../ui/theme/tokens.ts";
import { loadWorld } from "../world/load-world.ts";

declare global {
  interface Window {
    __eldrun?: AppProbe;
  }
}

applyPaletteTokens(document.documentElement);
// Malha neon (F59; o único estilo desde a F73): o tema da UI (`neon.css`) vale sob `body.malha`.
document.body.classList.add("malha");
// O acento da classe escolhida (F85, em toda parte); sem escolha ainda, o tema neutro (F79).
applyClassTheme(rememberedClass());

const params = new URLSearchParams(window.location.search);
const status = document.querySelector<HTMLElement>("#status");
const setStatus = (text: string) => {
  if (status) status.textContent = text;
};
const probe: AppProbe = { screen: "menu", ready: false, genVersion: GEN_VERSION };
window.__eldrun = probe;

/** `&tempo=0` (F51): relógio parado nas vitrines, para a sonda de sombreamento dar sempre o mesmo. */
const frozen = params.get("tempo") === "0";
/** `&estilo=0` (sem os termos da F51) ou `&estilo=fill:0.3,blob:0.6` (F51): estilo do toon nas vitrines. */
const style = parseToonStyle(params.get("estilo"));

/**
 * `&pos=baixo|medio|alto` (F52): o pós-processamento das vitrines, com as opções do preset (as
 * vitrines não leem as Opções); sem ele, o Médio, o padrão do jogo.
 */
function applyViewerPost(): void {
  const tier = params.get("pos");
  if (tier === "baixo" || tier === "medio" || tier === "alto") QUALITY.post = { tier, ...POST_PRESETS[tier], reduceFlashes: false };
}

/**
 * `?debug=ilha&seed=…&mode=…&fase=…&poi=…&montagem=…&recurso=…&bioma=…&detalhe=…&detalhes=0&tempo=0&estilo=…&bonecos=4&pos=…`:
 * visualizador da ilha sem servidor (depuração e e2e).
 */
async function bootIslandDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  const mode = params.get("mode") === "treta" ? "treta" : "mutirao";
  const seed = parseSeed(params.get("seed") ?? "SACI");
  const phase = Number(params.get("fase") ?? "0.15");
  probe.screen = "ilha";
  probe.seed = seed;
  probe.mode = mode;
  probe.models = () => models.stats();
  probe.avatarLoad = () => avatars.stats();
  const [world] = await Promise.all([loadWorld(seed, mode, (_, label) => setStatus(label)), models.preload(), avatars.preload(ALL_AVATARS)]);
  probe.worldHash = world.worldHash;
  probe.loadMs = world.loadMs;
  setStatus("");
  const look = params.get("montagem");
  const referenceClass = params.get("referencia");
  const time = params.get("instante");
  // `&quadro=x,z,yaw`: outra câmera para a captura de referência (m, m, rad).
  const quadro = (params.get("quadro") ?? "").split(",").map(Number);
  const frame = quadro.length === 3 && quadro.every(Number.isFinite) ? { x: quadro[0]!, z: quadro[1]!, yaw: quadro[2]! } : undefined;
  // `&heroi=ahead,side` (Fase 13): onde o herói fica em relação à câmera do quadro (m).
  const heroi = (params.get("heroi") ?? "").split(",").map(Number);
  const hero = heroi.length === 2 && heroi.every(Number.isFinite) ? { ahead: heroi[0]!, side: heroi[1]! } : undefined;
  // `&alvo=ahead,side` (Fase 14): onde fica o alvo do golpe (e para onde o herói olha), como `heroi`.
  const alvo = (params.get("alvo") ?? "").split(",").map(Number);
  const target = alvo.length === 2 && alvo.every(Number.isFinite) ? { ahead: alvo[0]!, side: alvo[1]! } : undefined;
  const reference: IslandReference | undefined = isClassId(referenceClass) ? {
    classId: referenceClass,
    clip: params.get("clipe") === "golpe" ? "golpe" : "parado",
    ...(time !== null && Number.isFinite(Number(time)) && { time: Math.max(0, Number(time)) }),
    ...(frame && { frame }),
    ...(hero && { hero }),
    ...(target && { target }),
  } : undefined;
  if (reference && !avatars.library?.has(classOf(reference.classId).character)) {
    setStatus("Modelo da classe indisponível para a captura de referência.");
    probe.error = "avatar da referência indisponível";
    world.pool.terminate();
    renderer.dispose();
    return;
  }
  const viewer = startIslandViewer(
    renderer,
    world,
    Number.isFinite(phase) ? phase : 0.15,
    params.get("poi") ?? undefined,
    params.get("recurso") ?? undefined,
    params.get("bioma") ?? undefined,
    look === null ? undefined : Number(look),
    { focus: params.get("detalhe") ?? undefined, off: params.get("detalhes") === "0" },
    frozen,
    style,
    Number(params.get("bonecos") ?? "0") || 0,
    avatars.library,
    reference,
    models.library,
  );
  probe.stats = () => viewer.stats();
  probe.shadingProbe = (options) => viewer.shading(options);
  probe.islandBlobTell = (options) => viewer.blobTell(options);
  probe.grassTell = () => viewer.grassTell();
  probe.gpuProfile = (frames) => viewer.gpuProfile(frames);
  probe.setGraphics = ({ shadows, ...post }) => {
    if (shadows !== undefined) QUALITY.shadows = shadows;
    QUALITY.post = { ...QUALITY.post, ...post };
  };
  const waitComplete = () => (viewer.complete ? (probe.ready = true) : requestAnimationFrame(waitComplete));
  waitComplete();
}

/**
 * `?debug=criaturas&fase=…&tempo=0&estilo=…&animadas=0|<n>&horda=<n>`: vitrine das criaturas, barras,
 * avisos e perigos, sem servidor; `animadas=0` deixa todas no boneco procedural, um número muda o teto
 * de animadas (F50); `horda` põe só uma roda de assombrações andando; `foco=maiores` aproxima a câmera dos
 * inimigos maiores e `lampejos=0` reduz os lampejos da assinatura deles (F91). Com `modelo=todos|<id>[,<id>…]` (F50),
 * a vitrine dos modelos animados: `clipe=`, `instante=`, `vista=frente|lado|costas|diagonal`, `boneco=0`,
 * `reforcada=1`.
 */
async function bootCreatureDebug(canvas: HTMLCanvasElement): Promise<void> {
  const phase = Number(params.get("fase") ?? "0.15");
  probe.screen = "criaturas";
  const model = params.get("modelo");
  if (model !== null) {
    const time = params.get("instante");
    const view = params.get("vista");
    const showcase = await startCreatureShowcase(createRenderer(canvas), {
      type: model === "todos" ? undefined : model,
      clip: params.get("clipe") ?? "parado",
      time: time === null || !Number.isFinite(Number(time)) ? undefined : Number(time),
      ...(params.get("instantes") !== null && { times: params.get("instantes")!.split(",").map(Number).filter(Number.isFinite) }),
      view: view === "lado" || view === "costas" || view === "diagonal" ? view : "frente",
      phase: Number.isFinite(phase) ? phase : 0.15,
      procedural: params.get("boneco") !== "0",
      elite: params.get("reforcada") === "1",
    });
    probe.stats = () => showcase.stats();
    setStatus("");
    requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
    return;
  }
  const animated = params.get("animadas");
  const horde = Number(params.get("horda") ?? "0");
  const viewer = startCreatureViewer(createRenderer(canvas), Number.isFinite(phase) ? phase : 0.15, frozen, style, {
    animated: animated !== "0",
    ...(animated !== null && animated !== "0" && Number.isFinite(Number(animated)) && Number(animated) > 1 && { cap: Number(animated) }),
    ...(Number.isFinite(horde) && horde > 0 && { horde: Math.min(80, Math.floor(horde)) }),
    reduceFlashes: params.get("lampejos") === "0",
    focus: params.get("foco") === "maiores",
  });
  probe.stats = () => viewer.stats();
  probe.shadingProbe = (options) => viewer.shading(options);
  probe.skinning = () => viewer.skinning();
  probe.blobTell = (ids) => viewer.blobTell(ids);
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

/**
 * `?debug=itens&item=…&vista=geral|mao|chao&fase=…&modelos=0&icones=0`: vitrine dos itens (F47), sem servidor:
 * na mão dos bonecos (`item` no primeiro), no chão e nos ícones; `modelos=0` mostra a reserva procedural.
 */
async function bootItemDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  const phase = Number(params.get("fase") ?? "0.15");
  const view = params.get("vista");
  probe.screen = "itens";
  probe.models = () => models.stats();
  const off = params.get("modelos") === "0";
  await Promise.all([off ? undefined : models.preload(), avatars.preload(ALL_AVATARS)]);
  const viewer = startItemViewer(renderer, off ? ModelLibrary.empty() : models.library, {
    item: params.get("item") ?? "machado-ferro",
    view: view === "mao" || view === "chao" ? view : "geral",
    phase: Number.isFinite(phase) ? phase : 0.15,
    icons: params.get("icones") !== "0",
  }, avatars.library);
  probe.stats = () => viewer.stats();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

/**
 * `?debug=chamas&fase=0.15&instante=1.2&lampejos=0&vista=perto|fenix&pos=&onda=1`: vitrine das chamas e da magia
 * (F93), sem servidor. O fogo e as brasas dos perigos, o fogo do Boitatá, o Círculo da Fênix e o acerto do cajado,
 * em laço de 4 s (`instante` para no ponto do laço, para capturas). Sem `pos`, o Alto (o bloom pega as chamas).
 * `lampejos=0` reduz os lampejos; `vista` aproxima a câmera; `onda=1` solta as ondas de choque do pós (F94).
 */
async function bootFlamesDebug(canvas: HTMLCanvasElement): Promise<void> {
  if (params.get("pos") === null) QUALITY.post = { tier: "alto", ...POST_PRESETS.alto, reduceFlashes: false };
  // Como na partida, a opção vale também para o pós.
  if (params.get("lampejos") === "0") QUALITY.post = { ...QUALITY.post, reduceFlashes: true };
  const renderer = createRenderer(canvas);
  const phase = Number(params.get("fase") ?? "0.15");
  const time = params.get("instante");
  probe.screen = "chamas";
  const showcase = startFlamesShowcase(renderer, {
    phase: Number.isFinite(phase) ? phase : 0.15,
    time: time === null || !Number.isFinite(Number(time)) ? undefined : Math.max(0, Number(time)),
    steady: params.get("lampejos") === "0",
    view: params.get("vista") === "perto" ? "perto" : params.get("vista") === "fenix" ? "fenix" : undefined,
    shock: params.get("onda") === "1",
  });
  probe.stats = () => showcase.stats();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

const STRUCTURE_VIEWS: readonly StructureViewerView[] = ["geral", "estacoes", "baus", "base", "forno", "fantasma", "longe"];

/**
 * `?debug=entulho&fase=0.15&quebra=1&lampejos=0&dissolve=0.4`: vitrine dos efeitos de cenário (F90), sem
 * servidor. Uma rocha, uma árvore e três peças quebram no instante `quebra` (s), e de novo a cada 8 s; o
 * entulho quica e assenta, e a camada ambiente (vagalumes de noite, poeira de dia) roda em volta. Na
 * fileira da frente, o que sai sem quebrar se desfaz (F95); `dissolve` o para nessa fração, para a
 * captura. `lampejos=0` reduz os lampejos.
 */
async function bootSceneryDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  const phase = Number(params.get("fase") ?? "0.15");
  const breakAt = Number(params.get("quebra") ?? "1");
  const dissolveAt = params.has("dissolve") ? Number(params.get("dissolve")) : NaN;
  probe.screen = "entulho";
  probe.models = () => models.stats();
  await models.preload();
  const showcase = startSceneryShowcase(renderer, models.library, {
    phase: Number.isFinite(phase) ? phase : 0.15,
    breakAt: Number.isFinite(breakAt) ? Math.max(0, breakAt) : 1,
    steady: params.get("lampejos") === "0",
    ...(Number.isFinite(dissolveAt) && { dissolveAt: Math.max(0, Math.min(1, dissolveAt)) }),
  });
  probe.stats = () => showcase.stats();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

/**
 * `?debug=estruturas&vista=geral|estacoes|baus|base|forno|fantasma|longe&tampa=aberta|fechada&aceso=0&fase=…&modelos=0`:
 * vitrine das estruturas (F48), sem servidor: estações, baús com a tampa, uma base pequena e os
 * fantasmas de construção; `modelos=0` mostra a reserva procedural.
 */
async function bootStructureDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  const phase = Number(params.get("fase") ?? "0.15");
  const view = params.get("vista") as StructureViewerView | null;
  probe.screen = "estruturas";
  probe.models = () => models.stats();
  const off = params.get("modelos") === "0";
  if (!off) await models.preload();
  const viewer = startStructureViewer(renderer, off ? ModelLibrary.empty() : models.library, {
    view: view && STRUCTURE_VIEWS.includes(view) ? view : "geral",
    phase: Number.isFinite(phase) ? phase : 0.15,
    open: params.get("tampa") === "aberta",
    lit: params.get("aceso") !== "0",
  });
  probe.stats = () => viewer.stats();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

/**
 * `?debug=avatares&personagem=mago&clipe=golpe&tempo=0.5&item=machado-ferro&vista=lado&capsula=1&mira=0.5&fase=…`:
 * vitrine dos avatares animados (F49), sem servidor: um personagem ou os 6, um clipe parado num
 * instante (`tempo`, s) ou em laço, o item na mão e a cápsula do jogador para comparar a escala. Com
 * `&andar=<m/s>&agachado=1&gestos=golpe:0.2:0.7:espada-prata,arco:0.7:2:arco-galho&duracao=<s>`, um
 * roteiro pela máquina de estados do jogo (`tempo` para o roteiro naquele instante; `vista=pes`, de
 * perfil rente ao chão). F120: `vista=direita|esquerda|quatro`, `superficie=0`, `vidro=&fresnel=&cor=`,
 * `fundo=transparente` e `painel=1`.
 */
/**
 * `?debug=neon&clipe=parado|golpe&instante=&pos=`: vitrine da malha monocromática com neon
 * (F58, sem servidor), com o bloom do Alto por padrão (`pos=` troca).
 */
/** Estação aberta junto do inventário na vitrine da UI (F82): um Forno, um baú ou nenhuma. */
const showcaseStation = (value: string | null) => (value === "forno" || value === "bau" ? value : undefined);
/** Painel da vitrine da UI pela URL (o inventário por padrão). */
const showcasePanel = (value: string | null) =>
  value === "fabricacao" || value === "habilidades" || value === "kit" || value === "pausa" || value === "opcoes" || value === "mapa" || value === "ficha" || value === "lobby" || value === "hud" ? value : "inventario";
/**
 * `?debug=ui&slot=0..3|-1&painel=inventario|fabricacao|habilidades|kit|pausa|opcoes|mapa|ficha|lobby|hud`:
 * vitrine da UI (F62, sem servidor), com dados de exemplo; `kit` é a página dos componentes do kit (F79);
 * `pausa` e `opcoes`, o menu de pausa e as Opções ao lado (F80); `mapa`, `ficha` e `lobby` são os painéis da
 * F82; `hud`, só o HUD da partida, sem painel (F81); `&estacao=forno|bau` abre um Forno ou um baú junto do
 * inventário (F82).
 */
async function bootUiDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  probe.screen = "ui";
  await models.preload();
  const slot = Number(params.get("slot") ?? "0");
  const showcase = startUiShowcase(renderer, models.library, { slot: Number.isInteger(slot) && slot >= -1 && slot <= 3 ? slot : 0, panel: showcasePanel(params.get("painel")), station: showcaseStation(params.get("estacao")) });
  probe.stats = () => showcase.stats();
  await showcase.ready;
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

async function bootNeonDebug(canvas: HTMLCanvasElement): Promise<void> {
  if (params.get("pos") === null) QUALITY.post = { tier: "alto", ...POST_PRESETS.alto, reduceFlashes: false };
  const renderer = createRenderer(canvas);
  probe.screen = "neon";
  await Promise.all([models.preload(), avatars.preload()]);
  const library = avatars.library;
  if (!library) {
    setStatus("Pacote dos avatares indisponível.");
    probe.error = "avatares indisponíveis";
    return;
  }
  const time = params.get("instante");
  const showcase = await startNeonShowcase(renderer, library, models.library, {
    clip: params.get("clipe") === "golpe" ? "golpe" : "parado",
    time: time === null || !Number.isFinite(Number(time)) ? undefined : Number(time),
    skills: params.get("habilidades") === "1",
    arrowView: params.get("vista") === "flecha" ? "lado" : params.get("vista") === "atirador" ? "atirador" : undefined,
  });
  probe.stats = () => showcase.stats();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

async function bootAvatarDebug(canvas: HTMLCanvasElement): Promise<void> {
  const renderer = createRenderer(canvas);
  probe.screen = "avatares";
  probe.models = () => models.stats();
  probe.avatarLoad = () => avatars.stats();
  await Promise.all([models.preload(), avatars.preload(ALL_AVATARS)]);
  const library = avatars.library;
  if (!library) {
    setStatus("Pacote dos avatares indisponível.");
    probe.error = "avatares indisponíveis";
    return;
  }
  const character = params.get("personagem");
  const clip = params.get("clipe") ?? "parado";
  const time = params.get("tempo");
  const phase = Number(params.get("fase") ?? "0.15");
  const viewer = startAvatarViewer(renderer, library, models.library, {
    character: character !== null && character in AVATAR_MODELS ? (character as AvatarModelId) : undefined,
    clip: clip in AVATAR_CLIPS ? (clip as keyof typeof AVATAR_CLIPS) : "parado",
    time: time === null || !Number.isFinite(Number(time)) ? undefined : Number(time),
    item: params.get("item") ?? "",
    view: avatarView(params.get("vista")),
    surface: surfaceFromParams(params),
    transparent: params.get("fundo") === "transparente",
    panel: params.get("painel") === "1",
    capsule: params.get("capsula") === "1",
    pitch: Number(params.get("mira") ?? "0") || 0,
    phase: Number.isFinite(phase) ? phase : 0.15,
    script: parseAvatarScript(params),
  });
  probe.stats = () => viewer.stats();
  probe.snapshot = () => viewer.snapshot();
  setStatus("");
  requestAnimationFrame(() => requestAnimationFrame(() => (probe.ready = true)));
}

/** A vista do visor dos avatares pela URL (`frente` por padrão). */
const AVATAR_VIEWS = ["frente", "costas", "lado", "pes", "direita", "esquerda", "quatro"] as const;
function avatarView(value: string | null): (typeof AVATAR_VIEWS)[number] {
  return AVATAR_VIEWS.find((v) => v === value) ?? "frente";
}

/** `?debug=sons`: bancada de escuta das amostras ao lado da síntese (F45), sem servidor nem WebGL. */
function bootSoundDebug(ui: HTMLElement): void {
  // O contexto nasce no primeiro gesto, como no jogo (e retoma antes do clique tocar).
  const unlock = () => audio.unlock();
  window.addEventListener("pointerdown", unlock, { capture: true });
  window.addEventListener("keydown", unlock, { capture: true });
  const viewer = startSoundViewer(ui);
  probe.screen = "sons";
  probe.stats = () => viewer.stats();
  probe.audio = () => audio.stats();
  setStatus("");
  probe.ready = true;
}

async function boot(): Promise<void> {
  const canvas = document.querySelector<HTMLCanvasElement>("#game");
  const ui = document.querySelector<HTMLElement>("#ui");
  if (!canvas || !ui) throw new Error("elementos #game/#ui não encontrados");
  if (params.get("debug") !== null) applyViewerPost();
  if (params.get("debug") === "ilha") return bootIslandDebug(canvas);
  if (params.get("debug") === "criaturas") return bootCreatureDebug(canvas);
  if (params.get("debug") === "itens") return bootItemDebug(canvas);
  if (params.get("debug") === "estruturas") return bootStructureDebug(canvas);
  if (params.get("debug") === "entulho") return bootSceneryDebug(canvas);
  if (params.get("debug") === "chamas") return bootFlamesDebug(canvas);
  if (params.get("debug") === "sons") return bootSoundDebug(ui);
  if (params.get("debug") === "avatares") return bootAvatarDebug(canvas);
  if (params.get("debug") === "neon") return bootNeonDebug(canvas);
  if (params.get("debug") === "ui") return bootUiDebug(canvas);
  const renderer = createRenderer(canvas);
  setStatus("");
  // Áudio (F29): efeitos pré-renderizados já no carregamento da página (não precisa de gesto); o
  // contexto nasce no primeiro gesto (política de autoplay) e todo botão faz um clique suave.
  // Opções (F30): volumes, qualidade e acessibilidade aplicados antes da primeira tela.
  settings.load();
  settings.attachRenderer(renderer);
  void audio.prerender();
  // Modelos importados (F37): baixam junto com a página; a partida espera por eles no carregamento.
  void models.preload();
  probe.models = () => models.stats();
  // Avatares animados (F49): pacote próprio, baixado junto; a partida espera por ele no carregamento.
  void avatars.preload();
  probe.avatarLoad = () => avatars.stats();
  const unlock = () => audio.unlock();
  window.addEventListener("pointerdown", unlock, { capture: true });
  window.addEventListener("keydown", unlock, { capture: true });
  document.addEventListener("click", (e) => {
    if (e.target instanceof Element && e.target.closest("button")) audio.play("ui-clique", { bus: "ui" });
  }, { capture: true });
  probe.audio = () => audio.stats();
  new AppFlow(renderer, ui, readClientConfig(import.meta.env, window.location.search), probe).showMenu();
}

boot().catch((error: unknown) => {
  const message = error instanceof UnsupportedError ? error.message : "Não foi possível iniciar o jogo.";
  probe.error = message;
  setStatus(message);
  console.error(error);
});
